import { useState, useEffect } from 'react';
import '@/App.css';
import { Countdown } from '@/components/Countdown';
import { ApplicationForm } from '@/components/ApplicationForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  CheckCircle,
  Users,
  TrendingUp,
  Target,
  FileText,
  BarChart3,
  Video,
  Mail,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import Success from '@/pages/Success';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getDeadline = () => {
  // 72 hours from today at 2:00 PM EST
  const now = new Date();
  const today = new Date(now);
  today.setHours(14, 0, 0, 0); // Set to 2pm today
  const deadline = new Date(today.getTime() + (72 * 60 * 60 * 1000)); // Add 72 hours
  return deadline.toISOString();
};

function App() {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [spotsRemaining, setSpotsRemaining] = useState(4);
  const [isScrolled, setIsScrolled] = useState(false);

  // Check if we're on the success page
  const isSuccessPage = window.location.pathname === '/success' || window.location.search.includes('session_id');

  // If success page, show that instead
  if (isSuccessPage) {
    return <Success />;
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch remaining spots
  useEffect(() => {
    const fetchSpots = async () => {
      try {
        const response = await axios.get(`${API}/audit/spots`);
        if (response.data && response.data.remaining !== undefined) {
          setSpotsRemaining(response.data.remaining);
        }
      } catch (error) {
        console.error('Failed to fetch spots:', error);
      }
    };
    fetchSpots();
    // Poll every 30 seconds
    const interval = setInterval(fetchSpots, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      console.log('Submitting to:', `${API}/audit/applications`);
      console.log('Form data:', formData);
      
      const response = await axios.post(`${API}/audit/applications`, formData);
      console.log('Application response:', response.data);
      
      if (response.data && response.data.id) {
        toast.success('Application received! Redirecting to payment...');
        
        const checkoutResponse = await axios.post(`${API}/audit/checkout/session`, {
          applicationId: response.data.id,
          email: formData.email,
        });
        console.log('Checkout response:', checkoutResponse.data);
        
        if (checkoutResponse.data && checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        } else {
          console.error('No checkout URL in response');
          toast.error('Payment setup failed. Please try again.');
          setIsSubmitting(false);
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      toast.error(error.response?.data?.detail || error.message || 'Submission failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    setShowApplicationForm(true);
    setTimeout(() => {
      const formElement = document.getElementById('application-section');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="App bg-white">
      <Toaster position="top-right" />
      
      {/* Fixed Header on Scroll */}
      {isScrolled && (
        <motion.div
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          className="fixed top-0 left-0 right-0 bg-[#1c334a] shadow-lg z-50 py-4"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
            <div className="flex items-center justify-between gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_b7df7e6c-e242-4f3e-81af-66fcd6ed0832/artifacts/z3oe8tks_Trilogy%20One%20Logo%20.png"
                alt="Trilogy One Logo"
                className="h-8 sm:h-10"
              />
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <Badge className="bg-transparent text-white px-3 py-1.5 text-xs font-medium border-2 border-white rounded-full pulse-badge">
                  {spotsRemaining} Spots Available
                </Badge>
                <Badge className="bg-transparent text-white px-3 py-1.5 text-xs font-medium border-2 border-white rounded-full pulse-badge">
                  Ends in 72 Hours
                </Badge>
                <Button
                  onClick={scrollToForm}
                  className="bg-[#d8e005] text-[#1c334a] hover:bg-transparent hover:text-white hover:border-2 hover:border-[#d8e005] rounded-2xl px-4 sm:px-6 py-2 font-black transition-all text-sm sm:text-base"
                >
                  Reserve Spot
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Hero Section */}
      <section
        className="relative bg-[#1c334a] overflow-hidden min-h-[90vh] flex items-center"
        data-testid="hero-section"
      >
        {/* Header with Logo and Countdown - WHITE BACKGROUND */}
        <div className="absolute top-0 left-0 right-0 z-20 py-6 bg-white shadow-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
            <div className="flex items-center justify-between">
              <div className="max-w-7xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_b7df7e6c-e242-4f3e-81af-66fcd6ed0832/artifacts/z3oe8tks_Trilogy%20One%20Logo%20.png"
                  alt="Trilogy One Logo"
                  className="h-10 sm:h-12"
                />
              </div>
              {/* Countdown Timer - Only on initial header, hidden on scroll */}
              {!isScrolled && (
                <div className="hidden lg:block">
                  <Countdown deadline={getDeadline()} />
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Background Image - positioned to show subjects on right */}
        <div 
          className="absolute inset-0 bg-cover bg-right"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_b7df7e6c-e242-4f3e-81af-66fcd6ed0832/artifacts/t1f8bpw4_image%20%2825%29.png)',
            backgroundPosition: 'right center',
          }}
        />
        
        {/* Gradient Overlay - opaque on left, transparent on right */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(28,51,74,0.95) 0%, rgba(28,51,74,0.85) 40%, rgba(28,51,74,0.4) 70%, rgba(28,51,74,0.2) 100%)',
          }}
        />
        
        {/* Motion blob decoration */}
        <motion.div
          className="absolute top-1/4 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl"
          style={{ background: '#049aaa' }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.18, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl relative z-10 py-12 sm:py-16 lg:py-24">
          <div className="grid grid-cols-1 gap-8 items-center">
            {/* Left content - now full width */}
            <div className="lg:max-w-3xl space-y-6 sm:space-y-8">
              <div className="space-y-3 sm:space-y-4">
                <p className="text-[#d8e005] text-xs sm:text-sm font-medium tracking-widest uppercase">
                  72-HOUR FLASH SALE
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white t1c-heading leading-tight lg:whitespace-nowrap">
                  Get Your Complete AI Roadmap
                </h1>
                <p className="text-base sm:text-lg text-white/90 t1c-body max-w-2xl">
                  Only four small businesses will secure their AI Opportunity Roadmap™ at half price — offer ends in 72 hours.
                </p>
              </div>

              <div className="space-y-3">
                {[
                  'Discover $50K+ in Annual Savings Opportunities',
                  'Get a Clear 90-Day AI Implementation Roadmap',
                  'Remote Delivery — No Travel Required',
                ].map((bullet, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 + 0.3 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#d8e005] flex-shrink-0 mt-0.5" />
                    <span className="text-white/90 text-sm sm:text-base t1c-body">{bullet}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="reserve-spot-primary-button"
                  onClick={scrollToForm}
                  className="bg-[#d8e005] text-[#1c334a] hover:bg-transparent hover:text-white hover:border-2 hover:border-[#d8e005] rounded-2xl px-6 py-3 text-base font-black transition-all"
                >
                  Reserve Your Spot Now →
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const detailsSection = document.getElementById('inside-roadmap-section');
                    if (detailsSection) {
                      detailsSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="bg-white/10 text-white border-2 border-white/50 hover:bg-white hover:text-[#1c334a] rounded-2xl px-6 py-3 text-base font-medium transition-all"
                >
                  See What's Inside
                </Button>
              </div>
            </div>

            {/* Right column image removed for full-width layout */}
          </div>
        </div>
      </section>

      {/* Offer Overview Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white" data-testid="offer-overview-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-[#1c334a] mb-4">
              Your 90-Day AI Roadmap — Tailored for Small Business
            </h2>
            <p className="text-base sm:text-lg t1c-body text-[#526981]">
              In just two weeks, Trilogy One will uncover where AI can streamline your operations, cut manual work, and drive real ROI. Normally $5,000, this exclusive offer is just $2,500 for a limited 72-hour window.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: Users,
                title: 'Remote Interviews',
                desc: 'Speak with your team to understand workflows',
              },
              {
                icon: BarChart3,
                title: 'Workflow Analysis',
                desc: 'Map your systems and identify bottlenecks',
              },
              {
                icon: TrendingUp,
                title: 'ROI Projections',
                desc: 'See time and cost savings with real numbers',
              },
              {
                icon: Target,
                title: '90-Day Plan',
                desc: 'Clear implementation roadmap tailored for you',
              },
              {
                icon: Video,
                title: 'Executive Readout',
                desc: 'Present findings to your leadership team',
              },
              {
                icon: FileText,
                title: 'Full Documentation',
                desc: 'Comprehensive report you can action immediately',
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="bg-white border border-[rgba(28,51,74,0.10)] rounded-2xl shadow-sm hover:shadow-md transition-shadow h-full">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-2xl bg-[rgba(4,154,170,0.10)] flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-[#049aaa]" />
                    </div>
                    <CardTitle className="text-xl t1c-heading text-[#1c334a]">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#526981] t1c-body">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Button
              data-testid="offer-cta-button"
              onClick={scrollToForm}
              className="bg-[#d8e005] text-[#1c334a] hover:bg-transparent hover:text-[#1c334a] hover:border-2 hover:border-[#d8e005] rounded-2xl px-6 py-3 text-base font-black transition-all"
            >
              Secure My Audit for $2,500 →
            </Button>
          </div>
        </div>
      </section>

      {/* Who Qualifies Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#f8f9fa]" data-testid="qualifies-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-[#1c334a] mb-8 text-center">
              Who This Offer Is For
            </h2>
            <p className="text-base sm:text-lg t1c-body text-[#526981] mb-8 text-center">
              This offer is for small business leaders who are ready to act and move fast. To qualify, your business must:
            </p>

            <div className="space-y-4 mb-8">
              {[
                'Have 10-20 employees',
                'Be available for remote stakeholder interviews within two weeks',
                'Submit a 50% deposit ($1,250) to secure your spot',
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-4 bg-white p-4 sm:p-6 rounded-2xl border border-[rgba(28,51,74,0.10)]"
                >
                  <CheckCircle className="w-6 h-6 text-[#049aaa] flex-shrink-0 mt-0.5" />
                  <span className="text-[#1c334a] text-base sm:text-lg t1c-body">{item}</span>
                </motion.div>
              ))}
            </div>

            <div className="text-center space-y-4">
              <Button
                data-testid="qualifies-cta-button"
                onClick={scrollToForm}
                className="bg-[#d8e005] text-[#1c334a] hover:bg-transparent hover:text-[#1c334a] hover:border-2 hover:border-[#d8e005] rounded-2xl px-6 py-3 text-base font-black transition-all"
              >
                Apply Now Before Spots Are Gone →
              </Button>
              <div className="flex justify-center">
                <Countdown deadline={getDeadline()} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside the Roadmap Section */}
      <section
        id="inside-roadmap-section"
        className="py-12 sm:py-16 lg:py-24 bg-white"
        data-testid="inside-roadmap-section"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-[#1c334a] mb-4">
              What You'll Experience Over Two Weeks
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                day: 'Days 1–3',
                title: 'Kickoff & Discovery',
                desc: 'Meet your team and pinpoint friction points.',
              },
              {
                day: 'Days 2–4',
                title: 'Systems Mapping',
                desc: 'Visualize your workflows and find bottlenecks.',
              },
              {
                day: 'Days 4–5',
                title: 'Opportunity Identification',
                desc: 'Spot where AI delivers biggest returns.',
              },
              {
                day: 'Day 6',
                title: 'Validation Workshop',
                desc: 'Align priorities and confirm next steps.',
              },
              {
                day: 'Days 6–8',
                title: 'ROI Modeling',
                desc: 'Attach numbers to each recommendation.',
              },
              {
                day: 'Day 10',
                title: 'Executive Readout',
                desc: 'Receive your custom AI Opportunity Roadmap™.',
              },
            ].map((phase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="bg-white border border-[rgba(28,51,74,0.10)] rounded-2xl shadow-sm hover:shadow-md transition-shadow h-full">
                  <CardHeader>
                    <div className="inline-block px-3 py-1 rounded-full bg-[rgba(4,154,170,0.10)] text-[#049aaa] text-xs font-medium mb-3">
                      {phase.day}
                    </div>
                    <CardTitle className="text-xl t1c-heading text-[#1c334a]">
                      {phase.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#526981] t1c-body">{phase.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* AI Opportunity Roadmap - 3 Phases Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#f8f9fa]" data-testid="roadmap-phases-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-[#1c334a] mb-6">
                What is the AI Opportunity Roadmap™?
              </h2>
              <p className="text-base sm:text-lg t1c-body text-[#526981] max-w-3xl mx-auto">
                A comprehensive 3-phase approach that transforms your business from AI uncertainty to sustained competitive advantage.
              </p>
            </div>

            {/* Three Phase Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Phase 1: Clarity */}
              <Card className="bg-white border-2 border-[#1c334a] rounded-2xl overflow-hidden">
                <CardHeader className="bg-[#d8e005] p-6">
                  <div className="text-[#526981] text-xs font-medium tracking-widest uppercase mb-2">PHASE ONE</div>
                  <CardTitle className="text-3xl font-black text-[#1c334a]">CLARITY</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 flex items-center justify-center">
                      <svg className="w-16 h-16 text-[#d8e005]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1c334a] text-center mb-4">Audit</h3>
                  <p className="text-[#526981] text-sm mb-4">
                    We'll identify where time, energy, and margin are being lost — and quantify where AI can drive immediate ROI.
                  </p>
                  <ul className="space-y-2 text-sm text-[#526981]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Map workflows & constraints</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Surface where time and margin are lost</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Rank opportunities by ROI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Set a focused 90-day plan</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Phase 2: Momentum */}
              <Card className="bg-white border-2 border-[#1c334a] rounded-2xl overflow-hidden">
                <CardHeader className="bg-[#d8e005] p-6">
                  <div className="text-[#526981] text-xs font-medium tracking-widest uppercase mb-2">PHASE TWO</div>
                  <CardTitle className="text-3xl font-black text-[#1c334a]">MOMENTUM</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 flex items-center justify-center">
                      <svg className="w-16 h-16 text-[#d8e005]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1c334a] text-center mb-4">Implement</h3>
                  <p className="text-[#526981] text-sm mb-4">
                    We'll help you deploy the right AI tools and automations to drive quick wins, boost team productivity and scale smarter.
                  </p>
                  <ul className="space-y-2 text-sm text-[#526981]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Implement highest-return fixes first</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Automate repetitive/manual tasks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Shorter cycles & fewer errors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Capacity gains without new headcount</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Phase 3: Resilience */}
              <Card className="bg-white border-2 border-[#1c334a] rounded-2xl overflow-hidden">
                <CardHeader className="bg-[#d8e005] p-6">
                  <div className="text-[#526981] text-xs font-medium tracking-widest uppercase mb-2">PHASE THREE</div>
                  <CardTitle className="text-3xl font-black text-[#1c334a]">RESILIENCE</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 flex items-center justify-center">
                      <svg className="w-16 h-16 text-[#d8e005]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1c334a] text-center mb-4">Optimize</h3>
                  <p className="text-[#526981] text-sm mb-4">
                    We help you stay ready for what's next by establishing feedback loops, adopting the right emerging tools, and positioning your business to adapt, lead, and grow with confidence.
                  </p>
                  <ul className="space-y-2 text-sm text-[#526981]">
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Build adaptive operating rhythm</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Quarterly reviews & recalibration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Curated signal tracking (hype vs impact)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#d8e005] mt-0.5">•</span>
                      <span>Pilot programs that prepare for what's next</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* CTA */}
            <div className="text-center">
              <p className="text-[#526981] text-lg mb-6">
                This offer expires in 72 hours or when four road maps are booked. Get the same high-value process larger firms pay $10K+ for — tailored for small businesses ready to act now.
              </p>
              <Button
                data-testid="roadmap-cta-button"
                onClick={scrollToForm}
                size="lg"
                className="bg-[#d8e005] text-[#1c334a] hover:bg-transparent hover:text-[#1c334a] hover:border-2 hover:border-[#d8e005] rounded-2xl px-8 py-4 text-lg font-black transition-all"
              >
                Secure My Spot for $2,500 →
              </Button>
              <p className="text-sm text-[#526981] mt-4">
                A 50% deposit ($1,250) secures your spot. Balance due at kickoff.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Always Start with an Audit Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#1c334a] gradient-noise" data-testid="always-audit-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-white mb-6">
              Why Start With an Audit?
            </h2>
            <p className="text-base sm:text-lg t1c-body text-white/90">
              The future will belong to companies who can effectively blend technology with human potential. A systematic audit is the proven foundation for identifying opportunities and avoiding costly missteps in AI implementation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#d8e005] flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-[#1c334a]" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg mb-2">McKinsey Global Institute</p>
                    <p className="text-white/70 text-sm">Research Study</p>
                  </div>
                </div>
                <p className="text-white/90 t1c-body text-base mb-4">
                  "Companies that strategically adopt AI and automation can increase their cash flow by up to 122% and see productivity gains of 40% or more within the first year."
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-white/10 backdrop-blur-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#d8e005] flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 text-[#1c334a]" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg mb-2">MIT Sloan Management Review</p>
                    <p className="text-white/70 text-sm">Research Study</p>
                  </div>
                </div>
                <p className="text-white/90 t1c-body text-base mb-4">
                  "Organizations that conduct systematic audits before implementing AI are 3.5x more likely to achieve their ROI targets and avoid costly missteps."
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { value: '$50K+', label: 'Potential Annual Savings' },
              { value: '10-14 Days', label: 'Audit Duration' },
              { value: '100%', label: 'Remote Delivery' },
              { value: '90 Days', label: 'Implementation Timeline' },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="space-y-2"
              >
                <p className="text-3xl sm:text-4xl font-black text-[#d8e005]">{stat.value}</p>
                <p className="text-white/70 text-sm t1c-body">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#f8f9fa]" data-testid="faq-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-[#1c334a] mb-12 text-center">
              Questions Before You Commit?
            </h2>

            <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-[5px]">
              {[
                {
                  q: 'What exactly is the AI Opportunity Roadmap™?',
                  a: 'It\'s a comprehensive 3-phase framework: Phase 1 (Audit) identifies your biggest opportunities, Phase 2 (Implement) deploys high-ROI solutions, and Phase 3 (Optimize) establishes continuous improvement systems. You receive a complete strategic plan with clear milestones and ROI projections.',
                },
                {
                  q: 'How long does the process take?',
                  a: 'Phase 1 (Audit) takes approximately 10-14 days. We conduct remote interviews, analyze your workflows, and deliver a comprehensive roadmap with ROI projections and implementation timeline.',
                },
                {
                  q: 'Do we need to prepare anything?',
                  a: 'Just a list of tools and 3–5 team contacts. We handle the rest. Our process is designed to minimize disruption to your daily operations.',
                },
                {
                  q: 'Can everything be done remotely?',
                  a: 'Yes — all sessions are virtual. We use secure video conferencing for interviews and workshops. No travel required.',
                },
                {
                  q: 'What happens after we complete the Roadmap?',
                  a: 'You\'ll have a clear roadmap to implement yourself or continue with Trilogy One for execution support. We provide full documentation and can offer ongoing partnership if needed.',
                },
                {
                  q: 'What if we don\'t qualify?',
                  a: 'This flash sale is specifically for businesses with 10-20 employees. If you\'re outside this range, we have other engagement models. Contact us directly.',
                },
                {
                  q: 'Is the $1,250 deposit refundable?',
                  a: 'Deposits become non-refundable once we schedule your audit and complete the onboarding process. This ensures serious commitment from both sides and protects the limited availability. If there are extenuating or unique circumstances, we\'re happy to discuss—we\'re reasonable people and will work with you on a case-by-case basis.',
                },
              ].map((faq, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="bg-white rounded-2xl border border-[rgba(28,51,74,0.10)] px-6"
                >
                  <AccordionTrigger className="text-left text-[#1c334a] font-medium hover:text-[#049aaa] hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#526981] t1c-body">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      {showApplicationForm && (
        <section
          id="application-section"
          className="py-12 sm:py-16 lg:py-24 bg-white"
          data-testid="application-section"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-[#1c334a] mb-4">
                  Secure Your Spot Now
                </h2>
                <p className="text-base sm:text-lg t1c-body text-[#526981]">
                  Fill out the form below to reserve your spot. You'll be redirected to secure payment to complete your $1,250 deposit.
                </p>
              </div>

              <div className="bg-[#f8f9fa] rounded-2xl p-6 sm:p-10">
                <ApplicationForm onSubmit={handleFormSubmit} isLoading={isSubmitting} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#049aaa]" data-testid="final-cta-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl t1c-heading text-white mb-3">
                Clarity Starts Here
              </h2>
              <p className="text-lg text-white/80 font-medium">
                Only Four Spots Available
              </p>
            </div>
            <p className="text-base sm:text-lg t1c-body text-white/90 mb-8">
              Over the next 72 hours, four small businesses will gain a custom plan to integrate AI and automation profitably. Once those spots are gone, the offer is closed for good.
            </p>
            
            <Button
              data-testid="final-cta-button"
              onClick={scrollToForm}
              size="lg"
              className="bg-[#d8e005] text-[#1c334a] hover:bg-transparent hover:text-white hover:border-2 hover:border-[#d8e005] rounded-2xl px-8 py-4 text-lg font-black transition-all"
            >
              Claim My Roadmap for $2,500 →
            </Button>
            
            <p className="text-white/80 text-sm">
              Save $2,500. Gain clarity, confidence, and measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#1c334a]" data-testid="footer-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="flex flex-col items-center space-y-6">
            <img
              src="https://customer-assets.emergentagent.com/job_b7df7e6c-e242-4f3e-81af-66fcd6ed0832/artifacts/z3oe8tks_Trilogy%20One%20Logo%20.png"
              alt="Trilogy One Logo"
              className="h-12 sm:h-16"
            />
            <p className="text-white/70 text-sm uppercase tracking-widest">
              Clarity. Momentum. Resilience.
            </p>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <Mail className="w-4 h-4" />
              <a href="mailto:Christian@trilogyoneconsulting.com" className="hover:text-white transition-colors">
                Christian@trilogyoneconsulting.com
              </a>
            </div>
            <div className="text-white/50 text-xs text-center">
              <p>© 2025 Trilogy One Consulting. All rights reserved.</p>
              <p className="mt-2">Limited-time offer. Terms and conditions apply.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;